import { useState, useRef } from "react";
import axios from "axios";
import Avatar from "./Avatar";
import AvatarLightbox from "./AvatarLightbox";
import { Button } from "./ui/button";
import { Camera, Trash2, Loader2, Expand } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AvatarUploader({ currentUrl, name, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(false);
  const fileInput = useRef(null);

  const pick = () => fileInput.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Datei zu groß (max 5 MB)");
      return;
    }
    setBusy(true); setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/uploads/avatar`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.avatar_url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    if (!currentUrl) return;
    setBusy(true); setError("");
    try {
      await axios.delete(`${API}/uploads/avatar`, { withCredentials: true });
      onChange?.("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-5" data-testid="avatar-uploader">
      <div className="relative">
        <button
          type="button"
          onClick={() => currentUrl && setLightbox(true)}
          title={currentUrl ? "Vergrößern" : ""}
          data-testid="avatar-expand-btn"
          className={currentUrl ? "cursor-zoom-in" : "cursor-default"}
        >
          <Avatar src={currentUrl} name={name} size={88} ring />
        </button>
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <Loader2 size={20} className="text-[#00e5ff] animate-spin" />
          </div>
        )}
        {currentUrl && !busy && (
          <span className="absolute bottom-0 right-0 w-6 h-6 bg-[#00e5ff] text-black flex items-center justify-center pointer-events-none">
            <Expand size={12} />
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-[0.26em] text-[#00e5ff]/80 font-outfit font-semibold mb-1">
          Profilfoto
        </p>
        <p className="text-xs text-white/50 leading-relaxed mb-3">
          JPG, PNG oder WebP, max. 5 MB. Wird auf Board, Chat & Profil angezeigt.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={pick}
            disabled={busy}
            data-testid="avatar-upload-btn"
            className="bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none font-outfit font-semibold h-10 px-4 text-sm"
          >
            <Camera size={14} className="mr-2" />
            {currentUrl ? "Foto ändern" : "Foto hochladen"}
          </Button>
          {currentUrl && (
            <Button
              type="button"
              onClick={remove}
              disabled={busy}
              variant="ghost"
              data-testid="avatar-remove-btn"
              className="border border-white/15 hover:border-red-400/50 hover:text-red-400 text-white/70 rounded-none h-10 px-4 text-sm font-outfit"
            >
              <Trash2 size={14} className="mr-2" />
              Entfernen
            </Button>
          )}
        </div>
        {error && <p className="text-red-400 text-xs mt-2" data-testid="avatar-error">{error}</p>}
      </div>
      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/bmp"
        onChange={onFile}
        className="hidden"
        data-testid="avatar-file-input"
      />
      {lightbox && currentUrl && (
        <AvatarLightbox src={currentUrl} name={name} subline="Dein Profilbild" onClose={() => setLightbox(false)} />
      )}
    </div>
  );
}
